import React, { useState, useCallback, useEffect } from "react";
import { connect } from "react-redux";
import PropTypes from "prop-types";
import { searchForGroup } from "../../actions/api";
import { Modal, Button, Alert, Tooltip } from "antd";
import { PlusOutlined, ExclamationCircleOutlined } from "@ant-design/icons";
import { ContentTable, SearchBar } from "@authzsvc/common-react-components";
import { errorAction, successAction, types as at } from "@authzsvc/api-lib";
import { showErrorNotification } from "../../util";
import { searchMaxLimit } from "../../constants";

/**
 * GroupSearchModal
 *
 * A modal component for searching and selecting groups.
 * Supports searching for groups with optional filtering and disabling specific groups from selection.
 *
 * @component
 * @param {Object} props
 * @param {string} props.title - Modal title.
 * @param {function} props.onSelect - Callback when a group is selected. Receives (record).
 * @param {function} props.searchForGroup - Redux action for searching groups.
 * @param {React.Element} [props.icon] - Optional icon for the trigger button.
 * @param {boolean} [props.isReplicaGroup] - If true, shows warning about replica group restrictions.
 * @param {string} [props.buttonText] - Text for the trigger button.
 * @param {Array<Object>} [props.nonAddableMembers] - Array of objects describing groups that cannot be selected.
 *   Each object should have:
 *     - {string} identifier (id of the group)
 *     - {string} message (reason why it cannot be selected)
 *
 * @returns {JSX.Element}
 */
const GroupSearchModal = ({
  onSelect,
  searchForGroup,
  title,
  icon,
  isReplicaGroup,
  buttonText,
  nonAddableMembers = [],
}) => {
  const [visible, setVisible] = useState(false);
  const [searchIsLoading, setSearchIsLoading] = useState(false);
  const [dataSource, setDataSource] = useState([]);
  const [searchValue, setSearchValue] = useState("");
  const [currentPage, setCurrentPage] = useState(1);

  const getNonAddable = (record) => {
    if (!nonAddableMembers || !record) return null;
    return nonAddableMembers.find(
      (item) => item.identifier === record.groupIdentifier
    );
  };

  const modalColumns = [
    {
      title: "ID",
      dataIndex: "groupIdentifier",
    },
    {
      title: "Name",
      dataIndex: "displayName",
    },
    {
      title: "Action",
      dataIndex: "id",
      render: (_, record) => {
        const nonAddable = getNonAddable(record);
        const addButton = (
          <Button
            id="recordButton"
            color="green"
            variant="solid"
            disabled={!!nonAddable}
            onClick={() => handleSelect(record)}
          >
            Add
          </Button>
        );
        return nonAddable ? (
          <Tooltip title={nonAddable.message}>{addButton}</Tooltip>
        ) : (
          addButton
        );
      },
    },
  ];

  const handleSelect = (record) => {
    setVisible(false);
    onSelect(record);
  };

  const openUpdateModal = (e) => {
    e.preventDefault();
    setVisible(true);
    setSearchValue("");
    setDataSource([]);
    setSearchIsLoading(false);
  };

  const handleCancel = () => {
    setVisible(false);
    setSearchIsLoading(false);
  };

  const handleSearch = useCallback(
    async (text, filter, limit) => {
      if (text.length >= 2) {
        setSearchIsLoading(true);
        const response = await searchForGroup({
          filter: `groupIdentifier:${filter}:${text.trim()}`,
          sort: "groupIdentifier",
          limit: searchMaxLimit,
        });

        if (response.type === successAction(at.SEARCH_GROUP)) {
          setSearchIsLoading(false);
          setDataSource(response.payload.data);
        } else if (response.type === errorAction(at.SEARCH_GROUP)) {
          showErrorNotification(
            response.payload.title,
            response.payload.data.message
          );
          setDataSource([]);
          setSearchIsLoading(false);
        }
      } else if (text.length === 0) {
        setDataSource([]);
        setSearchIsLoading(false);
      }
    },
    [searchForGroup]
  );

  const handleSearchValueChange = useCallback((value) => {
    setSearchValue(value);
  }, []);

  useEffect(() => {
    setCurrentPage(1);
  }, [dataSource]);

  return (
    <>
      <Button
        onClick={openUpdateModal}
        icon={icon || <PlusOutlined />}
        color="green"
        variant="solid"
        size="small"
      >
        {buttonText}
      </Button>
      <Modal
        style={{ minWidth: "90%" }}
        centered
        title={title}
        open={visible}
        onCancel={handleCancel}
        footer={null}
      >
        {isReplicaGroup && (
          <Alert
            type="warning"
            showIcon
            icon={<ExclamationCircleOutlined style={{ marginRight: 16 }} />}
            message="Selected group is of type 'Replica'. Please note that adding
                group members is not allowed. Any users added will be
                automatically removed during synchronization."
            style={{ marginBottom: 16 }}
          />
        )}
        <div style={{ marginTop: 16, marginBottom: 16 }}>
          <SearchBar
            placeholder="Search by group identifier..."
            onSearch={handleSearch}
            searchValue={searchValue}
            onSearchValueChange={handleSearchValueChange}
            filter={true}
            filterPlacement="before"
            searchOnClear={false}
            allowClear={true}
          />
        </div>
        {dataSource.length >= searchMaxLimit && (
          <Alert
            message="The search returned the maximum number of results. There might be more results that are not displayed. Please refine your search criteria."
            type="warning"
            showIcon
          />
        )}
        <ContentTable
          style={{ marginTop: "10px" }}
          loading={searchIsLoading}
          rowKey="id"
          pagination={{
            current: currentPage,
            onChange: (page) => setCurrentPage(page),
            showSizeChanger: true,
            hideOnSinglePage: true,
            pageSizeOptions: ["20", "50", "100", "500"],
            defaultPageSize: "20",
          }}
          columns={modalColumns}
          dataSource={dataSource}
        />
      </Modal>
    </>
  );
};

GroupSearchModal.propTypes = {
  title: PropTypes.string.isRequired,
  onSelect: PropTypes.func.isRequired,
  searchForGroup: PropTypes.func.isRequired,
  icon: PropTypes.element,
  isReplicaGroup: PropTypes.bool,
  buttonText: PropTypes.string,
  nonAddableMembers: PropTypes.arrayOf(
    PropTypes.shape({
      identifier: PropTypes.string.isRequired,
      message: PropTypes.string.isRequired,
    })
  ),
};

export default connect(null, { searchForGroup })(GroupSearchModal);
